<template>
  <div class="bc">
    <header class="bc__header">
      <h1 class="bc__title">Brand colours</h1>
      <p class="bc__intro">
        The brand kit's palette beside the primitives our tokens actually ship for
        <strong>{{ kit?.title ?? brand }}</strong
        >. A colour is marked <em>in tokens</em> when a generated token holds the same hex, <em>near</em> when one is
        within ΔE 3 (the same colour re-typed by hand), and <em>not in tokens</em> when none comes close — which is not
        automatically a bug, since the kit is the house palette and covers print too.
      </p>
      <div class="bc__actions">
        <button type="button" class="bc__button" @click="download('css')">Download palette as CSS</button>
        <button type="button" class="bc__button" @click="download('json')">Download palette as JSON</button>
        <span v-if="copied" class="bc__copied">Copied {{ copied }}</span>
      </div>
    </header>

    <div class="bc__columns">
      <section>
        <h2 class="bc__section-title">
          Brand kit palette
          <span v-if="drift.length" class="bc__count">
            {{ drift.filter((entry) => entry.match !== 'missing').length }}/{{ drift.length }} in tokens
          </span>
        </h2>

        <p v-if="!kit?.colours?.length" class="bc__empty">
          No brand kit palette for this brand. Its own primitives are still listed on the right.
        </p>

        <ul v-else class="bc__list">
          <li v-for="entry in drift" :key="entry.hex" class="bc__row">
            <button
              type="button"
              class="bc__swatch"
              :style="{ background: entry.hex }"
              :title="`Copy ${entry.hex}`"
              @click="copy(entry.hex)"
            />
            <span class="bc__row-body">
              <span class="bc__row-top">
                <span class="bc__name">{{ entry.name }}</span>
                <DriftBadge :match="entry.match" :delta="entry.delta" />
              </span>
              <span class="bc__meta">
                <code>{{ entry.hex }}</code>
                <span v-if="colourOf(entry.hex)?.cmyk">CMYK {{ colourOf(entry.hex)?.cmyk }}</span>
                <span v-if="colourOf(entry.hex)?.pms">PMS {{ colourOf(entry.hex)?.pms }}</span>
              </span>
              <code v-if="entry.token" class="bc__token">{{ entry.token }}</code>
            </span>
          </li>
        </ul>
      </section>

      <section>
        <h2 class="bc__section-title">
          Our primitives
          <span class="bc__count">{{ primitives.length }} literal hexes</span>
        </h2>

        <p class="bc__note">
          Every token in the brand's generated theme that holds a literal hex. Aliases are left out: they resolve to a
          token listed here. Marked when the brand kit documents the same colour.
        </p>

        <ul class="bc__list">
          <li v-for="primitive in primitives" :key="primitive.token" class="bc__row">
            <button
              type="button"
              class="bc__swatch"
              :style="{ background: primitive.hex }"
              :title="`Copy ${primitive.hex}`"
              @click="copy(primitive.hex)"
            />
            <span class="bc__row-body">
              <span class="bc__row-top">
                <code class="bc__token">{{ primitive.token }}</code>
                <span v-if="primitive.inKit" class="bc__in-kit">in brand kit</span>
              </span>
              <span class="bc__meta"><code>{{ primitive.hex }}</code></span>
            </span>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
  import DriftBadge from '../../figma-playground/DriftBadge.vue';
  import { DEFAULT_BRAND, extractTokenHexes, isBrand, loadBrandCss } from '../../figma-playground/brands';
  import { loadBrandKit } from '../../figma-playground/brand-kits';

  const route = useRoute();
  const brand = computed(() => (isBrand(route.query.brand) ? route.query.brand : DEFAULT_BRAND));

  // Plain refs with a watcher rather than useAsyncData: both sources are local files, and
  // a key interpolated at setup time would let a revisit serve another brand's payload.
  const kit = ref(await loadBrandKit(brand.value));
  const css = ref(await loadBrandCss(brand.value));

  watch(brand, async (name) => {
    const [nextKit, nextCss] = await Promise.all([loadBrandKit(name), loadBrandCss(name)]);
    if (brand.value !== name) return;

    kit.value = nextKit;
    css.value = nextCss;
  });

  const drift = computed(() => kit.value?.drift ?? []);

  const colourOf = (hex: string) => kit.value?.colours?.find((colour) => colour.hex === hex);

  const primitives = computed(() => {
    const kitHexes = new Set((kit.value?.colours ?? []).map((colour) => colour.hex));

    return [...extractTokenHexes(css.value ?? '')]
      .map(([token, hex]) => ({ token, hex, inKit: kitHexes.has(hex) }))
      .sort((left, right) => left.token.localeCompare(right.token));
  });

  const copied = ref('');

  async function copy(hex: string) {
    await navigator.clipboard.writeText(hex);
    copied.value = hex;
  }

  /**
   * Built in the browser from the snapshot, so a palette download needs no hosting and no
   * Drive folder — the brand kit has no download link for its colours at all.
   */
  function download(format: 'css' | 'json') {
    const colours = kit.value?.colours ?? [];

    const body =
      format === 'json'
        ? JSON.stringify({ brand: brand.value, title: kit.value?.title, colours }, null, 2)
        : [
            `/* ${kit.value?.title ?? brand.value} brand kit palette */`,
            ':root {',
            ...colours.map((colour) => `  --brand-${slug(colour.name)}: ${colour.hex.toLowerCase()};`),
            '}',
            '',
          ].join('\n');

    const url = URL.createObjectURL(new Blob([body], { type: format === 'json' ? 'application/json' : 'text/css' }));
    const link = document.createElement('a');

    link.href = url;
    link.download = `${brand.value}-palette.${format}`;
    link.click();

    URL.revokeObjectURL(url);
  }

  function slug(name: string) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  useHead({ title: () => `Colours · ${kit.value?.title ?? brand.value}` });
</script>

<style lang="postcss" scoped>
  .bc__header {
    margin-bottom: 2rem;
  }

  .bc__title {
    margin: 0 0 0.5rem;
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .bc__intro {
    max-width: 48rem;
    margin: 0 0 1rem;

    font-size: 0.875rem;
    line-height: 1.6;
    color: #727272;
  }

  .bc__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
  }

  .bc__button {
    cursor: pointer;

    padding: 0.375rem 0.75rem;
    border: 1px solid #d8d8d8;
    border-radius: 0.25rem;

    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    color: #545454;

    background: #fff;

    &:hover {
      background: #f2f2f2;
    }
  }

  .bc__copied {
    font-size: 0.75rem;
    color: #1a7f37;
  }

  .bc__columns {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(22rem, 1fr));
    gap: 2.5rem;
  }

  .bc__section-title {
    display: flex;
    gap: 0.75rem;
    align-items: baseline;

    margin: 0 0 0.75rem;

    font-size: 0.6875rem;
    font-weight: 700;
    color: #929292;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .bc__count {
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
  }

  .bc__note,
  .bc__empty {
    max-width: 30rem;
    margin: 0 0 1rem;

    font-size: 0.8125rem;
    line-height: 1.6;
    color: #727272;
  }

  .bc__list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    margin: 0;
    padding: 0;

    list-style: none;
  }

  .bc__row {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;

    padding: 0.5rem;
    border: 1px solid #f2f2f2;
    border-radius: 0.375rem;
  }

  .bc__swatch {
    cursor: pointer;

    flex: 0 0 auto;

    width: 2.75rem;
    height: 2.75rem;
    border: 1px solid #e4e4e4;
    border-radius: 0.25rem;
  }

  .bc__row-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.25rem;

    min-width: 0;
  }

  .bc__row-top {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
  }

  .bc__name {
    font-size: 0.875rem;
    font-weight: 600;
  }

  .bc__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;

    font-size: 0.6875rem;
    color: #727272;
  }

  .bc__token {
    font-family: 'SF Mono', 'Fira Code', Consolas, monospace;
    font-size: 0.6875rem;
    color: #545454;
    overflow-wrap: anywhere;
  }

  .bc__in-kit {
    padding: 0.125rem 0.4375rem;
    border-radius: 1rem;

    font-size: 0.625rem;
    font-weight: 600;
    color: #1a7f37;

    background: #eaf6ec;
  }
</style>
